"use client";
import { FeedbackSubmitHandler, useFeedbackFlowContextSelector } from "@/components/Feedback/FeedbackFlowContext";
import { FeedbackEvents } from "@/components/Feedback/feedbackPopupContent";
import { Flex, FlexRowAlignCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import Slide from "@mui/material/Slide";
import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import styles from "./feedbackRatingSelector.module.css";
import CircularFilledIcon from "@/components/CircularButton/styled/CircularFilledIcon";
import { X } from "lucide-react";
import { iconSizes } from "@/utils/iconProps";
import clsx from "clsx";
import { useCountUpTimer } from "@knowt/syncing/utils/hooks/useTimer";
import CircularProgress from "@/components/CircularProgress";

export type FloatingFeedbackSelectorProps = {
    title?: string;
    onSubmitHandler: FeedbackSubmitHandler;
    feedbackFor: FeedbackEvents;
    id?: string;
    direction?: "vertical" | "horizontal";
    isOpen?: boolean;
    autoCloseAfter?: number;
    onClose?: () => void;
    style?: React.CSSProperties;
    isStacked?: boolean;
};

const ratingEmojis = [
    `/images/emoji_sad.svg`,
    `/images/emoji_sad_mid.svg`,
    `/images/emoji_neutral.svg`,
    `/images/emoji_happy.svg`,
    `/images/emoji_love.svg`,
];

const FloatingFeedbackSelector = ({
    title,
    onSubmitHandler,
    feedbackFor,
    id,
    direction = "horizontal",
    isOpen: _isOpen,
    onClose,
    autoCloseAfter,
    style,
    isStacked = false,
}: FloatingFeedbackSelectorProps) => {
    const [isOpen, setIsOpen] = useState(_isOpen ?? false);

    const openFeedbackPopup = useFeedbackFlowContextSelector(state => state.openFeedbackPopup);
    const setSubmissions = useFeedbackFlowContextSelector(state => state.setSubmissions);
    const isFeedbackPopupOpen = useFeedbackFlowContextSelector(state => state.isFeedbackPopupOpen);

    const [hoveringEmojiIndex, setHoveringEmojiIndex] = useState<number | null>(null);

    const { elapsedTime, start, pause } = useCountUpTimer();

    const onCloseHandler = useCallback(() => {
        setIsOpen(false);
        setTimeout(() => {
            onClose?.();
        }, 1000);
    }, [onClose]);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        if (autoCloseAfter && !isFeedbackPopupOpen) {
            timer = setTimeout(onCloseHandler, autoCloseAfter);
            start();
        }

        if (isFeedbackPopupOpen) {
            clearTimeout(timer);
            pause();
        }

        return () => clearTimeout(timer);
    }, [autoCloseAfter, pause, start, isFeedbackPopupOpen, onCloseHandler]);

    const onRateHandler = (rating: number) => {
        if (rating < 5) {
            openFeedbackPopup({
                feedbackFor,
                rating,
                onSubmitHandler: ({ ...props }) => {
                    onSubmitHandler(props);
                    setSubmissions(prev => ({
                        ...prev,
                        [feedbackFor + id]: rating,
                    }));
                    onCloseHandler();
                },
            });
        } else {
            toast.success("Thank you for your feedback!");
            setSubmissions(prev => ({
                ...prev,
                [feedbackFor + id]: rating,
            }));
            onSubmitHandler({ rating, reason: "", category: "", followUp: "" });
            setIsOpen(false);
        }
    };

    const renderRatingButtons = () => (
        <FlexRowAlignCenter
            style={{
                gap: "1rem",
            }}>
            {ratingEmojis.map((emoji, index) => (
                <Flex
                    key={index}
                    style={{
                        transform: direction === "horizontal" ? "rotate(0deg)" : "rotate(270deg)",
                    }}>
                    <Image
                        style={{
                            cursor: "pointer",
                            transform: hoveringEmojiIndex === index ? "scale(1.1)" : "scale(1)",
                            transition: "transform 0.2s ease-in-out",
                        }}
                        onClick={() => {
                            onRateHandler(index + 1);
                        }}
                        src={emoji}
                        alt={"rating-emoji"}
                        width={33}
                        height={33}
                        onMouseEnter={() => setHoveringEmojiIndex(index)}
                        onMouseLeave={() => setHoveringEmojiIndex(null)}
                    />
                </Flex>
            ))}
        </FlexRowAlignCenter>
    );

    const renderCloseButton = () => {
        if (autoCloseAfter)
            return (
                <CircularProgress
                    variant="determinate"
                    value={(elapsedTime * 100) / autoCloseAfter}
                    size={30}
                    thickness={5}
                    color={themeColors.neutralWhite}
                    bgColor={themeColors.primary}
                    style={{
                        transform: direction === "horizontal" ? "rotate(270deg)" : "rotate(180deg)",
                    }}
                />
            );

        return (
            <CircularFilledIcon
                Icon={X}
                onClick={onCloseHandler}
                primaryBg={themeColors.primary}
                primaryColor={themeColors.neutral1}
                sx={{
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                        transform: "scale(1.1)",
                    },
                }}
                IconProps={{ strokeWidth: 3, size: iconSizes.LG }}
            />
        );
    };

    return (
        <div
            style={{
                position: "fixed",
                bottom: direction === "horizontal" ? "2rem" : "50%",
                right: direction === "horizontal" ? "50%" : "2rem",
                transform: direction === "horizontal" ? "translate(50%, 0)" : "translate(45%, 50%) rotate(90deg)",
                zIndex: 99999,
                ...style,
            }}>
            <Slide direction={direction === "horizontal" ? "up" : "down"} in={isOpen} unmountOnExit>
                <div>
                    <FlexRowAlignCenter
                        className={clsx(styles.floatingFeedbackSelector, styles.floatingFeedbackSelectorVertical)}
                        style={{
                            gap: "2rem",
                            borderRadius: isStacked ? "2.6rem" : undefined,
                        }}>
                        <FlexRowAlignCenter
                            className="bodyBold1"
                            style={{
                                gap: isStacked ? "1.6rem" : "3.4rem",
                                borderRight: "1px solid white",
                                padding: "0.8rem",
                                paddingRight: "3.4rem",
                                flexDirection:
                                    direction === "horizontal" ? (isStacked ? "column-reverse" : "row") : "row-reverse",
                            }}>
                            <p
                                className={direction === "horizontal" ? styles.floatingFeedbackSelectorTitle : ""}
                                style={{
                                    transform: direction === "horizontal" ? "rotate(0deg)" : "rotate(180deg)",
                                    whiteSpace: direction === "horizontal" ? "unset" : "nowrap",
                                    color: themeColors.neutralWhite,
                                }}>
                                {title}
                            </p>
                            {renderRatingButtons()}
                        </FlexRowAlignCenter>

                        {renderCloseButton()}
                    </FlexRowAlignCenter>
                </div>
            </Slide>
        </div>
    );
};

export default FloatingFeedbackSelector;
